import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SearchableSelectOption = {
  label: string;
  value: string;
  /** Extra words the filter matches but the list does not show (e.g. subject area). */
  keywords?: string;
  /**
   * Heading the option is listed under. Options of one group must be adjacent in `options`.
   * Typing the group's name finds every option in it.
   */
  group?: string;
};

export type SearchableSelectProps = {
  value: string;
  /** Values must be unique: a repeated value leaves stale rows in the list as it filters. */
  options: ReadonlyArray<SearchableSelectOption>;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Classes for the input, so the field matches the form it sits in. */
  inputClassName?: string;
  id?: string;
  "aria-label"?: string;
  noMatchesText?: string;
};

const DEFAULT_INPUT_CLASS =
  "tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300";

/** Every word of the query must appear somewhere in the label, group or keywords, in any order. */
export const matchesQuery = (option: SearchableSelectOption, query: string): boolean => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return true;
  }
  const haystack = `${option.label} ${option.group ?? ""} ${option.keywords ?? ""}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
};

export type ListPlacement = {
  left: number;
  width: number;
  maxHeight: number;
  /** Set when the list opens below the field. */
  top?: number;
  /** Set when the list opens above it: distance from the bottom of the viewport. */
  bottom?: number;
};

type Rect = { top: number; bottom: number; left: number; width: number };
type Viewport = { width: number; height: number };

const LIST_MAX_HEIGHT = 288;
const OPTION_HEIGHT = 36;
const LIST_GAP = 4;
const VIEWPORT_MARGIN = 8;
/** Table cells are narrow; the list may be wider than the field so names stay readable. */
const LIST_MIN_WIDTH = 240;

/**
 * Where the list goes. It is drawn over the page rather than inside the field's container,
 * so a scrolling table cannot clip it. It opens below the field unless the list would not
 * fit there and there is more room above.
 */
export const placeList = (field: Rect, viewport: Viewport, optionCount: number): ListPlacement => {
  const listHeight = Math.min(LIST_MAX_HEIGHT, Math.max(optionCount, 1) * OPTION_HEIGHT + 8);
  const roomBelow = viewport.height - field.bottom - LIST_GAP - VIEWPORT_MARGIN;
  const roomAbove = field.top - LIST_GAP - VIEWPORT_MARGIN;
  const width = Math.min(
    Math.max(field.width, LIST_MIN_WIDTH),
    viewport.width - 2 * VIEWPORT_MARGIN
  );
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(field.left, viewport.width - width - VIEWPORT_MARGIN)
  );
  if (roomBelow < listHeight && roomAbove > roomBelow) {
    return {
      left,
      width,
      bottom: viewport.height - field.top + LIST_GAP,
      maxHeight: Math.min(LIST_MAX_HEIGHT, roomAbove)
    };
  }
  return {
    left,
    width,
    top: field.bottom + LIST_GAP,
    maxHeight: Math.min(LIST_MAX_HEIGHT, Math.max(roomBelow, OPTION_HEIGHT * 2))
  };
};

/**
 * A select for long lists: type to narrow the options, then pick one with the mouse or the
 * arrow keys and Enter. Nothing changes until an option is picked; Escape or leaving the field
 * keeps the current value. To offer "none", include an option whose value is "".
 */
export function SearchableSelect({
  value,
  options,
  onChange,
  placeholder,
  disabled,
  inputClassName = DEFAULT_INPUT_CLASS,
  id,
  "aria-label": ariaLabel,
  noMatchesText = "No matches"
}: SearchableSelectProps): JSX.Element {
  const generatedId = useId();
  const listboxId = `${id ?? generatedId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<ListPlacement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((option) => option.value === value) ?? null;
  const matches = useMemo(
    () => options.filter((option) => matchesQuery(option, query)),
    [options, query]
  );

  const open = (nextQuery: string) => {
    setQuery(nextQuery);
    setIsOpen(true);
    const selectedIndex = nextQuery
      ? -1
      : options.findIndex((option) => option.value === value);
    setActiveIndex(Math.max(selectedIndex, 0));
  };

  const close = () => {
    setIsOpen(false);
    setQuery("");
  };

  const pick = (option: SearchableSelectOption) => {
    if (option.value !== value) {
      onChange(option.value);
    }
    close();
  };

  // Placed from the full option count, not the matches, so typing never flips the list
  // from below the field to above it. It follows the field when anything scrolls.
  const optionCount = options.length;
  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return undefined;
    }
    const place = () => {
      const field = inputRef.current?.getBoundingClientRect();
      if (!field) {
        return;
      }
      const next = placeList(
        field,
        { width: window.innerWidth, height: window.innerHeight },
        optionCount
      );
      // Scrolling the list itself lands here too; only re-render when the list has to move.
      setPlacement((previous) =>
        previous &&
        previous.left === next.left &&
        previous.width === next.width &&
        previous.maxHeight === next.maxHeight &&
        previous.top === next.top &&
        previous.bottom === next.bottom
          ? previous
          : next
      );
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isOpen, optionCount]);

  // Keep the highlighted option in view: on opening that is the current value, deep in a
  // long list; after that, wherever the arrow keys moved it. The list exists once placed.
  const isPlaced = placement !== null;
  useEffect(() => {
    if (isOpen && isPlaced) {
      listRef.current
        ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [isOpen, activeIndex, isPlaced]);

  const moveActive = (step: number) => {
    if (!matches.length) {
      return;
    }
    setActiveIndex((activeIndex + step + matches.length) % matches.length);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        open(query);
        return;
      }
      moveActive(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      if (isOpen && matches[activeIndex]) {
        event.preventDefault();
        pick(matches[activeIndex]);
      }
    } else if (event.key === "Escape") {
      if (isOpen) {
        event.preventDefault();
        close();
      }
    }
  };

  const activeOptionId =
    isOpen && matches[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined;

  const list =
    isOpen && placement ? (
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        style={placement}
        // A portal still bubbles React events up the component tree: keep a pick in the list
        // from reaching whatever row or panel holds the field, as a native select's list did.
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        className="tw-fixed tw-z-[10100] tw-m-0 tw-list-none tw-overflow-y-auto tw-overscroll-contain tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-p-1 tw-shadow-lg"
      >
        {matches.length ? (
          matches.map((option, index) => {
            const heading =
              option.group && option.group !== matches[index - 1]?.group ? option.group : null;
            return (
              <React.Fragment key={option.value}>
                {heading ? (
                  <li
                    role="presentation"
                    className="tw-px-3 tw-pb-1 tw-pt-2 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500"
                  >
                    {heading}
                  </li>
                ) : null}
                <li
                  id={`${listboxId}-option-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={option.value === value}
                  // mousedown, not click: a click would blur the input first and close the list
                  onMouseDown={(event) => {
                    event.preventDefault();
                    pick(option);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`tw-cursor-pointer tw-break-words tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm ${
                    index === activeIndex ? "tw-bg-slate-100" : ""
                  } ${
                    option.value === value
                      ? "tw-font-semibold tw-text-[#03234a]"
                      : "tw-text-slate-700"
                  }`}
                >
                  {option.label}
                </li>
              </React.Fragment>
            );
          })
        ) : (
          <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500">{noMatchesText}</li>
        )}
      </ul>
    ) : null;

  return (
    <div className="tw-relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        autoComplete="off"
        disabled={disabled}
        value={isOpen ? query : selected?.label ?? ""}
        placeholder={isOpen && selected ? selected.label : placeholder}
        title={selected?.label}
        onFocus={() => open("")}
        onClick={() => {
          if (!isOpen) {
            open("");
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
          setActiveIndex(0);
        }}
        onBlur={close}
        onKeyDown={handleKeyDown}
        // preflight is off, so an input keeps its inset border unless told otherwise
        className={`${inputClassName} tw-border-solid tw-pr-8`}
      />
      {/* Says "this is a list", as the native select's arrow did. Clicks go to the input. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="tw-pointer-events-none tw-absolute tw-right-2.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
      {list && typeof document !== "undefined" ? createPortal(list, document.body) : null}
    </div>
  );
}
